import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue } from '../../types';
import React, { useState } from 'react';
import { Timestamp, addDoc, collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';

import CSVReader from 'react-csv-reader';
import { db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

interface ImportError {
  type: string;
  source: string;
  message: string;
  voterName?: string;
  timestamp: Timestamp;
}

const DapaCSVImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [fullData, setFullData] = useState<string[][]>([]);

  const handleFileUpload = (data: any[], fileInfo: any) => {
    if (!data || data.length === 0) {
      setError('No data found in the CSV file');
      return;
    }
    
    // Limit preview to first 5 rows
    setPreviewData(data.slice(0, 5));
    // Store full data for import
    setFullData(data);
    setFile(fileInfo);
    setError(null);
  };

  const logImportError = async (errorData: ImportError) => {
    try {
      const importErrorsRef = collection(db, 'importErrors');
      await addDoc(importErrorsRef, errorData);
    } catch (error) {
      console.error('Error logging import error: ', error);
    }
  };

  const submitPollResponse = async (responses: Responses) => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: "sBB6eh2wcZMZLxNS6rBJ",
        pollTitle: "Dapa (VIS)",
        responses,
        submittedAt: Timestamp.now(),
        submittedBy: "Dapa Import",
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
    } catch (error) {
      console.error('Error submitting poll response: ', error);
      setError('Error submitting poll response: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const isVoterExists = async (voterName: string) => {
    try {
      const votersColRef = collection(db, 'voters');
      const q = query(votersColRef, where('name', '==', voterName));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking if voter exists: ', error);
      setError('Error checking if voter exists: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return false;
    }
  };

  const handleImport = async () => {
    debugger;
    if (!file) return;

    setLoading(true);
    setError(null);

    const batchRunTimestamp = Timestamp.now();

    try {
      // Use full data instead of preview data
      const rows = fullData.slice(1); // Skip header row

      for (const row of rows) {
        const voterName: string = row[0];
        if (voterName === "") {
          continue;
        }
        const voterExists = await isVoterExists(voterName);
        if (!voterExists) {
          // Log error for skipped voter
          await logImportError({
            type: 'VOTER_NOT_FOUND',
            source: 'Dapa Poll Import',
            message: `Voter not found in database`,
            voterName,
            timestamp: batchRunTimestamp
          });
          console.error(`Voter not found in database: ${voterName}`);
          continue;
        }

        const isRuaya: boolean = !!row[1];
        const isMatugas: boolean = !!row[2];

        if (isRuaya && isMatugas) {
          // Log error for unknown voter preference
          await logImportError({
            type: 'UNKNOWN_VOTER_PREFERENCE',
            source: 'Dapa Poll Import',
            message: `Voter has both Ruaya and Matugas preferences`,
            voterName,
            timestamp: batchRunTimestamp
          });
          console.error(`Voter has both Ruaya and Matugas preferences`);
          continue;
        }

        if (isRuaya) {
          const abante: Responses = {
            "DISTRICT 1 REPRESENTATIVE": "Lucille Sering",
            "MAYOR": "Peter Boy Ruaya",
            "PARTY": "Abante Surigao",
            "PROVINCIAL GOVERNOR": "Lyndon Barbers",
            "PROVINCIAL VICE GOVERNOR": "Geed Gokiangkee",
            "VOTER": voterName,
          };
          await submitPollResponse(abante);
          console.log(`Abante Surigao: ${voterName}`);
        } 
        if (isMatugas) {
          const padajon: Responses = {
            "DISTRICT 1 REPRESENTATIVE": "Lalo Matugas",
            "MAYOR": "Abeth Matugas",
            "PARTY": "Padajon",
            "PROVINCIAL GOVERNOR": "Bingo Matugas",
            "PROVINCIAL VICE GOVERNOR": "Bully Navarro IV",
            "VOTER": voterName,
          };
          await submitPollResponse(padajon);
          console.log(`Padajon: ${voterName}`);
        }
      }

      alert('Dapa Poll CSV Import Successful');
      setLoading(false);
    } catch (err) {
      setError('Failed to import CSV');
      console.error(err);
      setLoading(false);
    }
  };

  const papaparseOptions = {
    header: false,
    dynamicTyping: true,
    skipEmptyLines: true,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dapa Municipality CSV Import
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <CSVReader
        cssClass="csv-reader-input"
        onFileLoaded={handleFileUpload}
        parserOptions={papaparseOptions}
      />

      {previewData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {previewData[0].map((header, index) => (
                    <TableCell key={index}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.slice(1).map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Import CSV'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DapaCSVImport;
